import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSElement,
  WSText
} from "@wingspanhq/fe-component-library";
import { Engagement } from "@wingspanhq/wingspan-api-sdk";
import { ErrorAlert } from "@components/ErrorAlert";
import React from "react";
import { ActionEngagementDetails } from "../components/ActionEngagementDetails";
import { queriesEngagements } from "@query";

export interface ArchiveEngagementTypeModalProps {
  engagement: Engagement;
  onClose: () => void;
}

export const ArchiveEngagementTypeModal: React.FC<
  ArchiveEngagementTypeModalProps
> = ({ engagement, onClose }) => {
  const { openSnackbar } = useWSSnackbar();
  const [archiveEngagementType, updateEngagementTypeMeta] =
    queriesEngagements.archiveEngagement.useMutation();

  const handleArchive = async () => {
    await archiveEngagementType({
      engagementId: engagement.id!
    });

    openSnackbar({
      theme: "dark",
      type: "success",
      message: "Engagement archived"
    });
    onClose();
  };

  return (
    <>
      <WSText.ParagraphSm mb="L" weight="book">
        Are you sure you want to archive this engagement?
      </WSText.ParagraphSm>
      <ActionEngagementDetails engagement={engagement} />

      <WSAlert
        my="2XL"
        theme="info"
        icon="info-circle"
        size="M"
        title="Archiving engagement - what to expect:"
      >
        <WSElement as="ul" ml="L">
          <li>
            Any archived assignments associated with this engagement will remain
            archived and cannot be restored while the engagement remains
            archived.
          </li>
          <li>No changes can be made to this engagement.</li>
          <li>No additional invoices can be created for this engagement.</li>
          <li>
            Incomplete contractor requirements exclusively associated with this
            engagement will also be archived.
          </li>
        </WSElement>
        Existing invoices will remain on the Invoicing page until you delete
        them. You can always restore the engagement when needed.
      </WSAlert>

      <ErrorAlert
        error={updateEngagementTypeMeta.error}
        mb="XL"
        context="Engagement"
      />

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Secondary"
          },
          {
            label: "Archive engagement",
            loading: updateEngagementTypeMeta.isLoading,
            onAsyncClick: handleArchive,
            kind: "Primary"
          }
        ]}
      />
    </>
  );
};

export const useArchiveEngagementTypeModal = () => {
  return useWSModal(ArchiveEngagementTypeModal, {
    title: "Archive engagement",
    size: "M"
  });
};
